import repo from "../repository-data/grey/repository-grey-en.json";
import uuidv1  from "uuid/v1";
import { RepoAction } from './actions.js';
import { RepoState, Product, Tool, Dictionary, DKKPriceItem } from 'typings/repo';
import { Module, ConfigurationState } from 'typings/configuration';
import { PartType } from 'typings/globals.js';
import { getConfigurationProducts } from 'helpers/configurationHelper';

const initialRepo: RepoState = { ...repo, allProducts: getAllProducts(repo) };

export enum Material {
  Laminate = 'Laminate',
  Metal = 'Metal',
  Wood = 'Wood',
  MetalHD = 'MetalHD',
  WoodHD = 'WoodHD',
  Multiplex = 'Multiplex'
}

function patchProductPrices(products: Product[], prices: Dictionary<DKKPriceItem>): Product[] {
  return products.map(p => {
    const priceItem = prices[p.id];
    if (!priceItem) return p;
    if (p.prices['DKK'] === 0) return p; // structural/placeholder modules use DKK=0 as sentinel — do not patch
    const hasSale = priceItem.salePrice > 0 && priceItem.salePrice < priceItem.listPrice;
    const { TILBUD, ...pricesWithoutStaleSale } = p.prices; // drop any stale sale price before reapplying
    return {
      ...p,
      prices: {
        ...pricesWithoutStaleSale,
        DKK: priceItem.listPrice,
        ...(hasSale && { TILBUD: priceItem.salePrice }),
      },
    };
  });
}

function patchToolPrices(tools: Dictionary<Tool>, prices: Dictionary<DKKPriceItem>): Dictionary<Tool> {
  const result: Dictionary<Tool> = {};
  for (const key of Object.keys(tools)) {
    result[key] = { ...tools[key], items: patchProductPrices(tools[key].items, prices) };
  }
  return result;
}

const repoReducer = (state = initialRepo, action: RepoAction): RepoState =>
{
  switch (action.type)
  {
    case "SET_REPO":
      state = { ...action.repo, allProducts : getAllProducts(action.repo) };
      break;
    case "SET_PRICES": {
      const prices = action.prices;
      const patched: RepoState = {
        ...state,
        basecabinets: patchProductPrices(state.basecabinets, prices),
        basemodules:  patchProductPrices(state.basemodules, prices),
        cupboards:    patchProductPrices(state.cupboards, prices),
        tabletop:     patchProductPrices(state.tabletop, prices),
        toolboards:   patchProductPrices(state.toolboards, prices),
        extra:        patchProductPrices(state.extra, prices),
        step3:        patchToolPrices(state.step3, prices),
        step4:        patchToolPrices(state.step4, prices),
      };
      state = { ...patched, allProducts: getAllProducts(patched) };
      break;
    }
    case "RESET_PRICES": {
      let jsonRepo;
      try {
        jsonRepo = require(`../repository-data/${action.productLine}/repository-${action.productLine}-${action.langCode}.json`);
      } catch {
        jsonRepo = require(`../repository-data/grey/repository-grey-en.json`);
      }
      const allJsonProducts: Dictionary<Product> = getAllProducts(jsonRepo);
      const restoreJsonPrices = (products: Product[]): Product[] =>
        products.map(p => allJsonProducts[p.id] ? { ...p, prices: allJsonProducts[p.id].prices } : p);
      const restoreToolPrices = (tools: Dictionary<Tool>): Dictionary<Tool> => {
        const result: Dictionary<Tool> = {};
        for (const key of Object.keys(tools)) {
          result[key] = { ...tools[key], items: restoreJsonPrices(tools[key].items) };
        }
        return result;
      };
      const restored: RepoState = {
        ...state,
        basecabinets: restoreJsonPrices(state.basecabinets),
        basemodules:  restoreJsonPrices(state.basemodules),
        cupboards:    restoreJsonPrices(state.cupboards),
        tabletop:     restoreJsonPrices(state.tabletop),
        toolboards:   restoreJsonPrices(state.toolboards),
        extra:        restoreJsonPrices(state.extra),
        step3:        restoreToolPrices(state.step3),
        step4:        restoreToolPrices(state.step4),
      };
      state = { ...restored, allProducts: getAllProducts(restored) };
      break;
    }
    default:
      break;
  }
  return state;
}

export const searchProduct = (search: string, product: Product) => {
  return !search || `${product.id} ${product.title} ${product.subtitle}`.toLowerCase().indexOf(search.toLowerCase()) >= 0;
}

function getAllProducts(state: any) : Dictionary<Product> {
  return state.basecabinets
    .concat(state.basemodules)
    .concat(state.cupboards)
    .concat(state.extra)
    .concat(state.tabletop)
    .concat(state.toolboards)
    .concat(Object.values(state.step3 as Dictionary<Tool>).reduce<Product[]>((list, tool) => {list.push(...tool.items); return list; } ,[]))
    .concat(Object.values(state.step4 as Dictionary<Tool>).reduce<Product[]>((list, tool) => {list.push(...tool.items); return list; } ,[]))
    .reduce((dic: Dictionary<Product>, product: Product) => { dic[product.id] = product; return dic; },{});
}

export interface ModuleInfo {
  id: string;
  base?: Product;
  cupboard?: Product;
  cabinet?: Product;
  panel?: Product;
  tabletop?: Product;
  light: boolean;
}

export const getRandomModule = (repo: RepoState, configuration: ConfigurationState, corner: boolean, tabletop: Material, fullCabinet: boolean = false, canCart: boolean = false, canDouble: boolean = false, isLifted: boolean = false, lifter: string = "" ): Module =>
{
  return {
    id: uuidv1(),
    base: fullCabinet ? repo.basemodules.filter(m => m.isCorner === corner && m.prices['DKK'] !== 0 && (canDouble === m.isDouble || m.isDouble !== true))[0]?.id : repo.basemodules.filter(m => m.isCorner === corner && m.prices['DKK'] === 0 && (canDouble === m.isDouble || m.isDouble !== true))[0]?.id,
    cupboard: fullCabinet ? undefined : repo.cupboards.filter(c => c.isCorner === corner)[0].id,
    cabinet: fullCabinet ? undefined : corner ? repo.basecabinets.find(c => c.isCorner)?.id : getRandomCabinet(repo, configuration, false, canDouble),
    panel: !fullCabinet ? repo.toolboards.find(c => c.isCorner === corner)?.id : undefined,
    tabletop: fullCabinet ? undefined : tabletop,
    light: false,
    canCart: false,
    canFull: false,
    canCupboard: false,
    isLifted: isLifted,
    lifter: lifter
  };
}

export const getRandomCabinet = (repo: RepoState, configuration: ConfigurationState, includeCarts: boolean, canDouble: boolean) : string => {
  var parts = getConfigurationProducts(configuration, repo);

  var sortedParts = repo.basecabinets.filter(c => !c.isCorner && (!c.isCart || includeCarts) && (canDouble === c.isDouble || c.isDouble !== true)).map(c => c.id).sort((a: string, b: string) => (parts[a] || 0) - (parts[b] || 0));
  var least = parts[sortedParts[0]] || 0;
  sortedParts = sortedParts.filter(c => (parts[c] || 0) === least);

  return sortedParts[Math.floor(Math.random()*sortedParts.length)];
}

export const getRandomProduct = (repo: RepoState, corner: boolean, part_type: PartType) : Product =>
{
  const list = getProductList(repo, part_type).filter(p => (p.isCorner === true) === corner);
  return list[Math.floor(Math.random()*list.length)];
}

export const getProduct = (repo: RepoState, productId?: string) : Product | undefined =>
{
  if(!productId) return undefined;
  return repo.allProducts[productId];
}

export const getProductByIdAndType = (repo: RepoState, productId?: string, part_type?: PartType) => {
    if (!productId) return undefined;
    let products: Product[] = []
    switch(part_type){
        case "BASE": products = repo.basemodules.filter(x => x.isLifter !== true); break;
        case "CABINET": products = repo.basecabinets.filter(c => c.isCart !== true); break;
        case "CUPBOARD": products = repo.cupboards; break;
        case "LIGHT": products = repo.extra; break;
        case "PANEL": products = repo.toolboards; break;
        case "TABLETOP": products = repo.tabletop; break;
        case "LIFTER": products = repo.basemodules.filter(x => x.isLifter === true); break;
        case "LIFTERMODEL": products = repo.basemodules.filter(x => x.isLifter === true); break;
    }
    return products.find(x => x.id === productId)
}

const getProductList = (repo: RepoState, part_type: PartType) : Product[] => {
  switch(part_type){
    case "BASE": return repo.basemodules.filter(x => x.isLifter !== true);
    case "CABINET": return repo.basecabinets.filter(c => c.isCart !== true);
    case "CUPBOARD": return repo.cupboards;
    case "LIGHT": return repo.extra;
    case "PANEL": return repo.toolboards;
    case "TABLETOP": return repo.tabletop;
    case "LIFTER": return repo.basemodules.filter(x => x.isLifter === true);
    case "LIFTERMODEL": return repo.basemodules.filter(x => x.isLifter === true);
  }
}

export const getModuleInfo = (repo: RepoState, module: Module) : ModuleInfo =>
{
  return {
    id: module.id,
    base: getProduct(repo, module.base),
    cabinet: getProduct(repo, module.cabinet),
    cupboard: getProduct(repo, module.cupboard),
    //panel: getProduct(repo, module.panel),
    tabletop: getProduct(repo, module.tabletop),
    light: module.light,
  };
}

export const def = <T,Y>(obj: T | undefined, def: Y) : T | Y => {
  return obj ? obj : def;
}

export default repoReducer;
